import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";

const DISMISS_KEY = "hhl-pwa-install-dismissed-at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 3; // 3 days (short so users see it again)

function wasRecentlyDismissed() {
  const raw = typeof localStorage !== "undefined" && localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const t = parseInt(raw, 10);
  return !!t && Date.now() - t < DISMISS_TTL_MS;
}

/**
 * Lightweight bottom banner that appears once when the browser is ready to
 * install. It is now a *nudge* only — the permanent Install button in the
 * header is always available too.
 */
export default function InstallPrompt() {
  const { installable, installed, install } = useInstallPrompt();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (installed) { setVisible(false); return; }
    if (!installable) return;
    if (wasRecentlyDismissed()) return;
    setVisible(true);
  }, [installable, installed]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) { /* ignore */ }
    setVisible(false);
  };

  const doInstall = async () => {
    const res = await install();
    if (res.outcome === "accepted" || res.outcome === "dismissed") setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed z-40 bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 bg-white border border-zinc-300 shadow-2xl"
    >
      <div className="flex items-start gap-3 p-4">
        <img src="/icons/icon-192.png" alt="HHL" className="h-12 w-12 border border-zinc-200 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow">Install App</div>
          <div className="font-display font-black text-lg tracking-tight leading-tight mt-1">
            Get HHL on your home screen
          </div>
          <div className="text-xs text-zinc-600 mt-1">
            Faster, full-screen access. Works offline too.
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={doInstall}
              data-testid="pwa-install-btn"
              className="inline-flex items-center gap-2 bg-black text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-zinc-800"
            >
              <Download className="h-3.5 w-3.5" /> Install
            </button>
            <button
              onClick={dismiss}
              data-testid="pwa-dismiss-btn"
              className="text-[11px] uppercase tracking-wider font-semibold text-zinc-600 hover:text-black px-2 py-2"
            >
              Not Now
            </button>
          </div>
        </div>
        <button onClick={dismiss} className="p-1 text-zinc-400 hover:text-black" aria-label="Close">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
